import * as React from "react";
import { observer } from "mobx-react-lite";
import { Button, Typography } from "@material-tailwind/react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { userAccountController } from "../../../Common/Controllers/UserAccountController";
import { addressesController } from "../../../Common/Controllers/AddressesController";
import { alertController } from "../../../Common/Controllers/AlertController";
import { AddressCard } from "../Components/AddressCard";
import { AddressDialog } from "../Components/AddressDialog";
import { AddressDTO } from "../../../Common/DTOs/Address/AddressDTO";

export const AddressSubpage = observer(() => {
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState<AddressDTO | null>(null);

  useEffect(() => {
    if (!userAccountController.isLoggedIn && !userAccountController.isUserAccountDataLoading) {
      alertController.displayAlert("Login required", "You have to be logged in in order to view this page.", "warning", 4000);
      navigate("/latest");
      return;
    }
  }, []);

  const handleEdit = (address: AddressDTO) => {
    setEditingAddress(address);
    setDialogOpen(true);
  };

  const handleDelete = async (address: AddressDTO) => {
    if (confirm(`Are you sure you want to delete "${address.description}"?`)) {
      await addressesController.deleteAddress(address.id);
      alertController.displayAlert("Address deleted", null, "success", 3000);
    }
  };

  const handleAddNew = () => {
    setEditingAddress(null);
    setDialogOpen(true);
  };

  const handleSubmit = async (data: AddressDTO) => {
    if (editingAddress) {
      await addressesController.editAddress(data);
      alertController.displayAlert("Address updated", null, "success", 3000);
    } else {
      await addressesController.addAddress(data);
      alertController.displayAlert("Address added", null, "success", 3000);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingAddress(null);
  };

  return (
    <div className="animate-fadeIn">
      <div className="flex flex-col gap-8 w-full h-full justify-start items-center">
        <Typography variant="h3" color="white">Billing Address</Typography>
        {addressesController.billingAddress && (
          <AddressCard address={addressesController.billingAddress} onEdit={handleEdit} />
        )}
        <Typography variant="h3" color="white" className="mt-10">Delivery Addresses</Typography>
        <div className="flex flex-wrap justify-center gap-6">
          {addressesController.deliveryAddresses.length > 0 ?
            addressesController.deliveryAddresses.map((addr) => (
              <AddressCard key={addr.id} address={addr} onEdit={handleEdit} onDelete={handleDelete} />
            )) :
            <Typography color="gray" className="text-gray-500">No delivery addresses found.</Typography>
          }
        </div>

        <Button size="lg" onClick={handleAddNew} className="bg-purple mt-8">
          Add New Delivery Address
        </Button>
      </div>

      <AddressDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        initialData={editingAddress ?? undefined}
        onSubmit={handleSubmit}
      />
    </div>
  );
});
